import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/components/AuthProvider';
import { useCabinetId } from '@/hooks/useCabinetMemberMe';
import { apiClient } from '@/lib/apiClient';
import { ErrorService } from '@/lib/errors/ErrorService';
import type {
  ConversationFeedbackResponse,
  MessageFeedbackState,
} from '@hector/shared-types';

/**
 * Charge les votes (pouce) de l'utilisateur courant pour une conversation, afin
 * de rallumer les pouces déjà donnés à l'ouverture. Renvoie une Map indexée par
 * messageId. Best-effort : en cas d'échec, on renvoie une Map vide (les pouces
 * repartent neutres — dégradation silencieuse acceptable, le vote reste en base).
 *
 * @param conversationId - La conversation à charger, ou null pour vider l'état.
 * @param options.skipConversationId - Conversation créée localement à l'instant :
 *   sa ligne n'est pas encore persistée et aucun vote ne peut exister → on évite
 *   le GET inutile. Même contrat que `useConversationHistory`.
 */
export function useConversationFeedback(
  conversationId: string | null,
  options?: { skipConversationId?: string | null },
): Map<string, MessageFeedbackState> {
  const { user } = useAuth();
  const cabinetId = useCabinetId();
  // Conversation fraîchement créée (phantom) : pas encore en DB, inutile d'aller
  // chercher des votes inexistants. Aligné sur `useConversationHistory`.
  const skip = !!conversationId && options?.skipConversationId === conversationId;

  const feedbackQuery = useQuery({
    // Feedback is private to one authenticated member in one cabinet. Including
    // both identities prevents a short-lived cross-account cache leak on logout
    // or super-admin cabinet swap while the shared QueryClient stays mounted.
    queryKey: [
      'case-discovery-v3',
      'conversation-feedback',
      user?.id ?? null,
      cabinetId,
      conversationId,
    ],
    enabled: !!user?.id && !!cabinetId && !!conversationId && !skip,
    // L'écran du chat peut être remonté pendant qu'une résidence Canvas change.
    // Les votes ne font pas partie de cette transition : une fenêtre courte suffit
    // à dédupliquer ces remontages sans rendre un vote durablement obsolète.
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<ConversationFeedbackResponse> => {
      if (!conversationId) return [];

      try {
        return await apiClient.get<ConversationFeedbackResponse>(
          `/case-discovery-v3/conversations/${conversationId}/feedback`,
        );
      } catch (error) {
        // 404 = conversation pas encore persistée. Cas NORMAL et non-erreur : une
        // conversation naît paresseusement au premier `user_message` (le front
        // génère l'UUID avant l'envoi), donc entre ce moment et l'upsert backend
        // le GET feedback tombe légitimement sur une conversation inexistante —
        // qui n'a de toute façon aucun vote. Le remonter en toast (« La ressource
        // demandée est introuvable ») effrayait l'avocat pour rien, en plein
        // parcours de numérotation embarqué (constaté 2026-07-17). On l'avale.
        const statusCode =
          error instanceof Error && 'statusCode' in error
            ? (error as { statusCode?: number }).statusCode
            : undefined;
        if (statusCode === 404) return [];
        // Autre échec (5xx, réseau) : dégradation silencieuse conforme au contrat
        // best-effort du hook — tracké côté PostHog, mais jamais de toast (les
        // pouces repartent neutres, le vote reste en base).
        ErrorService.handle(error, {
          context: { action: `load_conversation_feedback:${conversationId}` },
          silent: true,
        });
        return [];
      }
    },
  });

  return useMemo(
    () => new Map((feedbackQuery.data ?? []).map((state) => [state.messageId, state])),
    [feedbackQuery.data],
  );
}
